import React, { useState, useEffect, useCallback, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEdit, faUserCheck, faUsers, faClock, faTags, faTag, faPlus, faTimes, faLock } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { formatDate, formatDateTime, SUPPORT_LEVEL_OPTIONS, formatDurationMinutes, formatWaitingTimeFromDate } from './utils';

const SidebarTicketDetails = ({
  ticket,
  isLoading = false,
  onTicketUpdated,
  participants: propParticipants,
  isLoadingParticipants: propIsLoadingParticipants,
  onReloadParticipants
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto, idCatTipoUsuario } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();

  const isAdmin = Number(idCatTipoUsuario) === 1;

  // Estado para nivel de soporte
  const [isUpdatingLevel, setIsUpdatingLevel] = useState(false);

  // Estado para cambio de responsable (solo admin)
  const [isEditingResponsible, setIsEditingResponsible] = useState(false);
  const [isUpdatingResponsible, setIsUpdatingResponsible] = useState(false);
  const [agentsList, setAgentsList] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Estado para colaboradores / participantes (si no vienen por props)
  const [localParticipants, setLocalParticipants] = useState([]);
  const [localIsLoadingParticipants, setLocalIsLoadingParticipants] = useState(false);

  const participants = propParticipants !== undefined ? propParticipants : localParticipants;
  const isLoadingParticipants = propIsLoadingParticipants !== undefined ? propIsLoadingParticipants : localIsLoadingParticipants;

  const ticketId = ticket?.id || ticket?.idTicket;

  // Estado para incidencias / etiquetas del ticket
  const [ticketTags, setTicketTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [catalogTags, setCatalogTags] = useState([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [isSavingTag, setIsSavingTag] = useState(false);
  const [removingTagId, setRemovingTagId] = useState(null);

  // Cargar etiquetas asignadas al ticket
  const fetchTicketTags = useCallback(async () => {
    if (!ticketId || !token) return;
    setIsLoadingTags(true);
    try {
      const resp = await fetch(`/api/SupportEngine/GetTicketEtiquetas?idTicket=${ticketId}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setTicketTags(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al consultar etiquetas del ticket:', err);
    } finally {
      setIsLoadingTags(false);
    }
  }, [ticketId, token]);

  // Cargar catálogo de etiquetas
  const fetchCatalogTags = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch('/api/SupportEngine/GetEtiquetas', {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setCatalogTags(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al consultar catálogo de etiquetas:', err);
    }
  }, [token]);

  useEffect(() => {
    if (ticketId) {
      fetchTicketTags();
    }
  }, [ticketId, fetchTicketTags]);

  const handleStartAddingTag = () => {
    setIsAddingTag(true);
    if (catalogTags.length === 0) {
      fetchCatalogTags();
    }
  };

  const handleAssignTag = async idEtiqueta => {
    const parsedId = parseInt(idEtiqueta);
    if (!parsedId || !ticketId || isSavingTag) return;

    setIsSavingTag(true);
    try {
      const resp = await fetch('/api/SupportEngine/AssignTicketEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idEtiqueta: parsedId,
          vCreatedUser: nombreCompleto || 'Sistema'
        })
      });

      if (resp.ok) {
        await fetchTicketTags();
        setIsAddingTag(false);
        if (onTicketUpdated) onTicketUpdated();
      } else {
        showErrorToast('Error al asignar incidencia.');
      }
    } catch (err) {
      showErrorToast('Error de conexión al asignar incidencia.');
    } finally {
      setIsSavingTag(false);
    }
  };

  const handleRemoveTag = async idEtiqueta => {
    const parsedId = parseInt(idEtiqueta);
    if (!parsedId || !ticketId || removingTagId) return;

    setRemovingTagId(parsedId);
    try {
      const resp = await fetch('/api/SupportEngine/RemoveTicketEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idEtiqueta: parsedId
        })
      });

      if (resp.ok) {
        showSuccessToast('Incidencia desasignada del ticket.');
        await fetchTicketTags();
        if (onTicketUpdated) onTicketUpdated();
      } else {
        showErrorToast('Error al desasignar incidencia.');
      }
    } catch (err) {
      showErrorToast('Error de conexión al desasignar incidencia.');
    } finally {
      setRemovingTagId(null);
    }
  };

  const availableTags = catalogTags.filter(ct => !ticketTags.some(tt => Number(tt.idEtiqueta) === Number(ct.idEtiqueta)));

  // Cargar participantes del ticket solo si se gestionan localmente
  const fetchParticipants = useCallback(async () => {
    if (propParticipants !== undefined) {
      if (onReloadParticipants) onReloadParticipants();
      return;
    }
    if (!ticketId || !token) return;

    setLocalIsLoadingParticipants(true);
    try {
      const resp = await fetch(`/api/SupportEngine/GetTicketParticipants?idTicket=${ticketId}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setLocalParticipants(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al consultar participantes:', err);
    } finally {
      setLocalIsLoadingParticipants(false);
    }
  }, [propParticipants, onReloadParticipants, ticketId, token]);

  // Cargar participantes solo si se gestionan localmente y cambia el ticketId
  useEffect(() => {
    if (propParticipants === undefined && ticketId) {
      fetchParticipants();
    }
  }, [propParticipants, fetchParticipants, ticketId]);

  // Cargar lista de agentes para reasignar
  const fetchAgentsList = useCallback(async () => {
    if (!isAdmin || !token) return;
    setIsLoadingAgents(true);
    try {
      const resp = await fetch('/api/SupportEngine/GetAgentsList', {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setAgentsList(Array.isArray(data) ? data : []);
      } else {
        const err = await resp.json().catch(() => ({}));
        showErrorToast(err.message || 'Error al cargar lista de asesores');
      }
    } catch (err) {
      showErrorToast('Error de conexión al cargar asesores: ' + err.message);
    } finally {
      setIsLoadingAgents(false);
    }
  }, [isAdmin, token]);

  useEffect(() => {
    if (isAdmin) {
      fetchAgentsList();
    }
  }, [fetchAgentsList, isAdmin]);

  const hasResponsible = Boolean(ticket?.idUsuarioAsignado && Number(ticket.idUsuarioAsignado) > 0);
  const canEditResponsible = isAdmin && hasResponsible;

  const handleStartEditingResponsible = () => {
    if (!canEditResponsible) return;
    setIsEditingResponsible(true);
    if (agentsList.length === 0 && !isLoadingAgents) {
      fetchAgentsList();
    }
  };

  const handleChangeResponsible = async e => {
    const newAgentId = parseInt(e.target.value);
    if (!newAgentId || Number(newAgentId) === Number(ticket.idUsuarioAsignado) || isUpdatingResponsible) return;

    setIsUpdatingResponsible(true);
    try {
      const response = await fetch('/api/SupportEngine/ChangeTicketResponsible', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idNuevoUsuarioAsignado: newAgentId
        })
      });

      if (response.ok) {
        showSuccessToast('Responsable actualizado exitosamente');
        setIsEditingResponsible(false);
        if (onTicketUpdated) onTicketUpdated();
        if (onReloadParticipants) {
          onReloadParticipants();
        } else {
          fetchParticipants();
        }
      } else {
        const err = await response.json().catch(() => ({}));
        showErrorToast(err.message || 'Error al cambiar responsable');
      }
    } catch (err) {
      showErrorToast('Error de red: ' + err.message);
    } finally {
      setIsUpdatingResponsible(false);
    }
  };

  const handleLevelChange = async e => {
    e.persist();
    const newLevel = parseInt(e.target.value);
    if (!newLevel || newLevel === ticket.idCatNivelSoporte || isUpdatingLevel) return;

    setIsUpdatingLevel(true);
    try {
      const response = await fetch('/api/SupportEngine/UpdateTicketSupportLevel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idCatNivelSoporte: newLevel
        })
      });

      if (response.ok) {
        showSuccessToast('Nivel de soporte actualizado');
        if (onTicketUpdated) onTicketUpdated();
      } else {
        const err = await response.json().catch(() => ({}));
        showErrorToast(err.message || 'Error al actualizar el nivel de soporte');
      }
    } catch (err) {
      showErrorToast('Error de red: ' + err.message);
    } finally {
      setIsUpdatingLevel(false);
    }
  };

  const hasResponded = ticket.iTiempoRespuestaMinutos !== null && ticket.iTiempoRespuestaMinutos !== undefined;

  // Refrescar el contador en frontend cada 60 segundos mientras el ticket siga sin contestar
  const [, setTick] = useState(0);
  useEffect(() => {
    if (hasResponded || !ticket?.dCreatedDate) return;

    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 60000);

    return () => clearInterval(timer);
  }, [hasResponded, ticket?.dCreatedDate]);

  const sidebarResponseTime = hasResponded
    ? formatDurationMinutes(ticket.iTiempoRespuestaMinutos)
    : formatWaitingTimeFromDate(ticket.dCreatedDate);

  return (
    <div>
      <div
        className="fw-semibold mb-2 pb-1 d-flex align-items-center justify-content-between"
        style={{ fontSize: '0.68rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b' }}
      >
        <span>DETALLES DEL TICKET {ticketId ? `#${ticketId}` : ''}</span>
        {(isLoading || isUpdatingResponsible || isUpdatingLevel || isLoadingParticipants || isLoadingAgents || isSavingTag || Boolean(removingTagId)) && (
          <span className="d-inline-flex align-items-center gap-1 text-primary fw-medium" style={{ fontSize: '0.65rem', textTransform: 'none' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.62rem' }} />
            <span>Actualizando...</span>
          </span>
        )}
      </div>

      {isLoading && !ticketId ? (
        <div
          className="p-3 rounded-2 border text-center py-4 shadow-none"
          style={{ backgroundColor: isDark ? '#121e2d' : '#ffffff', borderColor: isDark ? '#233142' : '#e2e8f0' }}
        >
          <FontAwesomeIcon icon={faSpinner} spin className="text-primary mb-2" style={{ fontSize: '1.2rem' }} />
          <div className="fw-medium" style={{ fontSize: '0.76rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            Cargando detalles del ticket...
          </div>
        </div>
      ) : (
        <div
          className="p-2 px-3 rounded-2 border position-relative overflow-hidden"
          style={{
            backgroundColor: isDark ? '#0b1727' : '#fcfcfc',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            transition: 'opacity 0.2s ease',
            opacity: (isLoading || isUpdatingResponsible || isUpdatingLevel || isSavingTag || Boolean(removingTagId)) ? 0.85 : 1
          }}
        >
          {/* Barra sutil de carga superior durante actualizaciones sin parpadeos */}
          {(isLoading || isUpdatingResponsible || isUpdatingLevel || isSavingTag || Boolean(removingTagId)) && (
            <div className="se-subtle-loading-bar" />
          )}
          <div className="d-flex flex-column gap-1" style={{ fontSize: '0.78rem' }}>
          {/* Fila 1: Asesor Asignado (Responsable Principal) */}
          <div className="py-1">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="fw-medium d-flex align-items-center gap-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                <span>Asesor Responsable</span>
                {isAdmin && (
                  <span className="badge bg-primary-soft text-primary p-0 px-1" style={{ fontSize: '0.62rem' }}>Admin</span>
                )}
              </span>
              {(isUpdatingResponsible || isLoadingAgents) && (
                <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '0.65rem' }} />
              )}
            </div>

            {isEditingResponsible && canEditResponsible ? (
              <div className="d-flex align-items-center gap-1">
                <div className="position-relative flex-grow-1">
                  <select
                    className="form-select form-select-sm border py-1 ps-2 pe-4"
                    style={{
                      fontSize: '0.76rem',
                      borderRadius: '6px',
                      backgroundColor: isDark ? '#182635' : '#ffffff',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      color: isDark ? '#f9fafd' : '#0f172a',
                      cursor: 'pointer'
                    }}
                    value={ticket.idUsuarioAsignado || ''}
                    onChange={async e => {
                      await handleChangeResponsible(e);
                    }}
                    disabled={isUpdatingResponsible}
                    autoFocus
                  >
                    <option value="" disabled>
                      {isLoadingAgents && agentsList.length === 0 ? 'Cargando asesores...' : agentsList.length === 0 ? 'Sin asesores disponibles' : 'Seleccionar nuevo responsable'}
                    </option>
                    {/* Mantener la opción del asesor actual mientras carga o si no está en la lista */}
                    {ticket.idUsuarioAsignado && !agentsList.some(a => String(a.idUsuario) === String(ticket.idUsuarioAsignado)) && (
                      <option value={ticket.idUsuarioAsignado}>
                        {ticket.vNombreUsuarioAsignado || `Asesor #${ticket.idUsuarioAsignado}`}
                      </option>
                    )}
                    {agentsList.map(a => (
                      <option key={a.idUsuario} value={a.idUsuario}>
                        {a.vNombreCompleto || a.vNombre || `Usuario #${a.idUsuario}`}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm px-2 py-0"
                  style={{
                    height: '28px',
                    fontSize: '0.72rem',
                    borderRadius: '6px',
                    borderColor: isDark ? '#233142' : '#ced4da',
                    color: isDark ? '#cbd5e1' : '#64748b'
                  }}
                  onClick={() => setIsEditingResponsible(false)}
                  title="Cancelar edición"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div
                className={`d-flex align-items-center justify-content-between px-2 py-1 rounded border ${
                  canEditResponsible ? 'hover-bg-200 cursor-pointer' : ''
                }`}
                style={{
                  minHeight: '28px',
                  cursor: canEditResponsible ? 'pointer' : (!hasResponsible ? 'not-allowed' : 'default'),
                  backgroundColor: isDark ? '#121e2d' : '#ffffff',
                  borderColor: isDark ? '#233142' : '#e2e8f0',
                  opacity: !hasResponsible ? 0.75 : 1
                }}
                onClick={canEditResponsible ? handleStartEditingResponsible : undefined}
                title={
                  !hasResponsible
                    ? 'Campo bloqueado: el ticket no tiene asesor responsable asignado'
                    : (isAdmin ? 'Clic para cambiar responsable (Solo Administradores)' : 'Asesor asignado al ticket')
                }
              >
                <div className="d-flex align-items-center gap-2 text-truncate">
                  <FontAwesomeIcon
                    icon={hasResponsible ? faUserCheck : faLock}
                    className={hasResponsible ? 'text-success' : 'text-muted'}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <span className="fw-semibold text-truncate" style={{ fontSize: '0.78rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                    {ticket.vNombreUsuarioAsignado || (hasResponsible ? `Asesor #${ticket.idUsuarioAsignado}` : 'Sin asignar')}
                  </span>
                </div>
                {canEditResponsible ? (
                  <FontAwesomeIcon icon={faEdit} className="text-muted ms-1" style={{ fontSize: '0.70rem' }} />
                ) : (
                  !hasResponsible && (
                    <FontAwesomeIcon icon={faLock} className="text-muted ms-1" style={{ fontSize: '0.68rem', opacity: 0.7 }} />
                  )
                )}
              </div>
            )}
          </div>

          {/* Fila 2: Participantes / Colaboradores del Ticket */}
          {participants && participants.length > 0 && (
            <>
              <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />
              <div className="py-1">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="fw-medium d-flex align-items-center gap-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: '0.68rem', color: '#6366f1' }} />
                    <span>Colaboradores ({participants.length})</span>
                  </span>
                  {isLoadingParticipants && (
                    <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '0.65rem' }} />
                  )}
                </div>

                <div className="d-flex flex-column gap-1">
                  {participants.map(p => (
                    <div
                      key={p.idTicketParticipante || p.idUsuario}
                      className="d-flex align-items-center gap-2 px-2 py-1 rounded border"
                      style={{
                        backgroundColor: isDark ? '#121e2d' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#e2e8f0'
                      }}
                      title={`Participante desde: ${formatDateTime(p.dCreatedDate)}`}
                    >
                      <div
                        className="rounded-circle fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: '24px',
                          height: '24px',
                          fontSize: '0.7rem',
                          backgroundColor: isDark ? 'rgba(14, 165, 233, 0.2)' : '#e0f2fe',
                          color: isDark ? '#38bdf8' : '#0284c7'
                        }}
                      >
                        {(p.vNombreCompleto || 'A')[0]}
                      </div>
                      <div className="d-flex flex-column min-w-0 flex-grow-1">
                        <span className="text-truncate" style={{ fontSize: '0.75rem', fontWeight: 500, color: isDark ? '#f9fafd' : '#0f172a' }}>
                          {p.vNombreCompleto || `Usuario #${p.idUsuario}`}
                        </span>
                        {p.dCreatedDate && (
                          <span className={isDark ? 'text-400' : 'text-muted'} style={{ fontSize: '0.65rem', lineHeight: '1.1' }}>
                            {formatDateTime(p.dCreatedDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />

          {/* Fila 3: Nivel de Soporte */}
          <div className="py-1">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="fw-medium" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                Nivel de Soporte
              </span>
              {isUpdatingLevel && (
                <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '0.65rem' }} />
              )}
            </div>

            <div className="position-relative">
              <select
                className="form-select form-select-sm border py-1 ps-2 pe-4"
                style={{
                  fontSize: '0.76rem',
                  borderRadius: '6px',
                  backgroundColor: isDark ? '#182635' : '#ffffff',
                  borderColor: isDark ? '#233142' : '#ced4da',
                  color: isDark ? '#f9fafd' : '#0f172a',
                  cursor: isUpdatingLevel ? 'not-allowed' : 'pointer'
                }}
                value={ticket.idCatNivelSoporte || ''}
                onChange={handleLevelChange}
                disabled={isUpdatingLevel}
              >
                <option value="" disabled>Seleccionar nivel</option>
                {SUPPORT_LEVEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />

          {/* Fila: Incidencias / Etiquetas */}
          <div className="py-1">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="fw-medium d-flex align-items-center gap-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faTags} style={{ fontSize: '0.68rem', color: '#3b82f6' }} />
                <span>Incidencias ({ticketTags.length})</span>
              </span>
              <div className="d-flex align-items-center gap-1">
                {(isLoadingTags || isSavingTag || Boolean(removingTagId)) && (
                  <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '0.65rem' }} />
                )}
                {!isAddingTag && (
                  <button
                    type="button"
                    className="btn btn-sm p-0 text-primary border-0 bg-transparent fw-semibold"
                    style={{ fontSize: '0.70rem' }}
                    onClick={handleStartAddingTag}
                    title="Asignar incidencia al ticket"
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-0.5" /> Asignar
                  </button>
                )}
              </div>
            </div>

            {/* Selector rápido para añadir incidencia */}
            {isAddingTag && (
              <div className="d-flex align-items-center gap-1 mb-2">
                <div className="position-relative flex-grow-1">
                  <select
                    className="form-select form-select-sm border py-0 ps-2 pe-4 w-100"
                    style={{
                      height: '28px',
                      fontSize: '0.74rem',
                      borderRadius: '6px',
                      backgroundColor: isDark ? '#182635' : '#ffffff',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      color: isDark ? '#f9fafd' : '#0f172a'
                    }}
                    defaultValue=""
                    disabled={isSavingTag}
                    onChange={e => {
                      if (e.target.value) handleAssignTag(e.target.value);
                    }}
                    autoFocus
                  >
                    <option value="" disabled>
                      {isSavingTag
                        ? 'Asignando incidencia...'
                        : availableTags.length === 0
                        ? 'Sin incidencias disponibles'
                        : 'Seleccionar incidencia...'}
                    </option>
                    {availableTags.map(tag => (
                      <option key={tag.idEtiqueta} value={tag.idEtiqueta}>
                        {tag.vNombre}
                      </option>
                    ))}
                  </select>
                  {isSavingTag && (
                    <div
                      className="position-absolute top-50 translate-middle-y"
                      style={{ right: '24px', pointerEvents: 'none' }}
                    >
                      <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '0.70rem' }} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm px-2 py-0"
                  style={{
                    height: '28px',
                    fontSize: '0.72rem',
                    borderRadius: '6px',
                    borderColor: isDark ? '#233142' : '#ced4da',
                    color: isDark ? '#cbd5e1' : '#64748b'
                  }}
                  disabled={isSavingTag}
                  onClick={() => setIsAddingTag(false)}
                  title="Cancelar"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Lista de chips / badges asignados */}
            {ticketTags.length > 0 ? (
              <div className="d-flex flex-wrap gap-1">
                {ticketTags.map(tag => {
                  const isCurrentTagRemoving = removingTagId === Number(tag.idEtiqueta);
                  return (
                    <span
                      key={tag.idEtiqueta}
                      className="badge px-2.5 py-1 rounded-pill fw-semibold d-inline-flex align-items-center"
                      style={{
                        backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                        color: isDark ? '#cbd5e1' : '#334155',
                        border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                        fontSize: '0.74rem',
                        gap: '6px',
                        maxWidth: '230px',
                        overflow: 'hidden',
                        opacity: isCurrentTagRemoving ? 0.6 : 1,
                        transition: 'opacity 0.15s ease'
                      }}
                      title={tag.vDescripcion || tag.vNombre}
                    >
                      <FontAwesomeIcon icon={faTag} style={{ fontSize: '0.65rem', color: isDark ? '#94a3b8' : '#64748b' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tag.vNombre}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm p-0 border-0 bg-transparent ms-1"
                        style={{
                          color: isDark ? '#94a3b8' : '#64748b',
                          lineHeight: 1,
                          fontSize: '0.72rem',
                          cursor: isCurrentTagRemoving ? 'not-allowed' : 'pointer'
                        }}
                        disabled={isCurrentTagRemoving || isSavingTag}
                        onClick={() => handleRemoveTag(tag.idEtiqueta)}
                        title={isCurrentTagRemoving ? 'Quitando incidencia...' : 'Quitar incidencia'}
                      >
                        {isCurrentTagRemoving ? (
                          <FontAwesomeIcon icon={faSpinner} spin className="text-primary" style={{ fontSize: '0.65rem' }} />
                        ) : (
                          <FontAwesomeIcon icon={faTimes} />
                        )}
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : !isAddingTag && (
              <div
                className="text-muted fst-italic px-2 py-1 rounded border border-dashed"
                style={{
                  fontSize: '0.74rem',
                  borderColor: isDark ? '#233142' : '#e2e8f0',
                  backgroundColor: isDark ? '#101c26' : '#f8fafc'
                }}
              >
                Sin incidencias asignadas
              </div>
            )}
          </div>

          <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />

          {/* Fila 4: Creado y Última Actividad */}
          <div className="row g-1 py-1">
            <div className="col-6">
              <div style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>Creado</div>
              <div style={{ fontSize: '0.76rem', color: isDark ? '#cbd5e1' : '#334155', fontWeight: 500 }}>
                {formatDateTime(ticket.dCreatedDate)}
              </div>
            </div>
            <div className="col-6">
              <div style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>Última Actividad</div>
              <div style={{ fontSize: '0.76rem', color: isDark ? '#cbd5e1' : '#334155', fontWeight: 500 }}>
                {formatDate(ticket.dUltimaActividad)}
              </div>
            </div>
          </div>

          <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />

          {/* Fila 5: 1ra Respuesta / Tiempo sin contestar */}
          <div className="py-1">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="fw-medium" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                {hasResponded ? '1ra Respuesta' : 'Tiempo sin contestar'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-1">
              {hasResponded ? (
                <span
                  className="badge px-2 py-1 rounded d-inline-flex align-items-center gap-1"
                  style={{
                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#dcfce7',
                    color: isDark ? '#34d399' : '#15803d',
                    fontSize: '0.74rem',
                    fontWeight: 600
                  }}
                  title="Tiempo que tardó el asesor en enviar el primer mensaje"
                >
                  <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.70rem' }} />
                  <span>1er mensaje en {sidebarResponseTime}</span>
                </span>
              ) : sidebarResponseTime ? (
                <span
                  className="badge px-2 py-1 rounded d-inline-flex align-items-center gap-1"
                  style={{
                    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : '#ffedd5',
                    color: isDark ? '#fb923c' : '#c2410c',
                    fontSize: '0.74rem',
                    fontWeight: 600
                  }}
                  title="Tiempo transcurrido en espera del primer mensaje del asesor"
                >
                  <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.70rem' }} />
                  <span>Sin contestar: {sidebarResponseTime}</span>
                </span>
              ) : (
                <span className={isDark ? 'text-400' : 'text-muted'} style={{ fontSize: '0.74rem' }}>
                  Sin contestar
                </span>
              )}
            </div>
          </div>

          {Number(ticket.iEstadoTicket) === 2 && (ticket.iTiempoResolucionMinutos ?? ticket.iTiempoCompletadoMinutos) !== null && (ticket.iTiempoResolucionMinutos ?? ticket.iTiempoCompletadoMinutos) !== undefined && (
            <>
              <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />
              <div className="py-1">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="fw-medium" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Tiempo de Resolución
                  </span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <span
                    className="badge px-2 py-1 rounded d-inline-flex align-items-center gap-1"
                    style={{
                      backgroundColor: isDark ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                      color: isDark ? '#818cf8' : '#4338ca',
                      fontSize: '0.74rem',
                      fontWeight: 600
                    }}
                    title={`Ticket cerrado. Tiempo de atención/resolución: ${formatDurationMinutes(ticket.iTiempoResolucionMinutos ?? ticket.iTiempoCompletadoMinutos)}`}
                  >
                    <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.70rem' }} />
                    <span>Resuelto en {formatDurationMinutes(ticket.iTiempoResolucionMinutos ?? ticket.iTiempoCompletadoMinutos)}</span>
                  </span>
                </div>
              </div>
            </>
          )}

          {ticket.vMotivoCerrado && (
            <>
              <hr className="my-1" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }} />
              <div className="py-1">
                <span className="fw-medium" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                  Motivo de Cierre
                </span>
                <div
                  className="p-2 rounded border mt-1"
                  style={{
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#e2e8f0',
                    color: isDark ? '#cbd5e1' : '#334155'
                  }}
                >
                  {ticket.vMotivoCerrado}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default SidebarTicketDetails;
